import { Routes, Route, Outlet, Navigate } from 'react-router-dom'
import { PublicShell } from './components/motion'
import { ProtectedRoute } from './routes/ProtectedRoute'
import { DashboardLayout } from './components/layout/dashboard-layout'

// Pages
import HomePage from './pages/Home'
import LoginPage from './pages/auth/Login'
import RegisterPage from './pages/auth/Register'
import ForgotPasswordPage from './pages/auth/ForgotPassword'
import DashboardPage from './pages/dashboard/Dashboard'
import TemplateLibraryPage from './pages/dashboard/templates/TemplateLibrary'
import TemplateDetailPage from './pages/dashboard/templates/TemplateDetail'
import TemplateEditPage from './pages/dashboard/templates/TemplateEdit'
import TemplateNewPage from './pages/dashboard/templates/TemplateNew'
import TemplateAnalyticsPage from './pages/dashboard/templates/TemplateAnalytics'
import HubDetailPage from './pages/dashboard/hubs/HubDetail'
import AssetsPage from './pages/dashboard/Assets'
import DocumentsPage from './pages/dashboard/documents/Documents'
import DocumentDetailPage from './pages/dashboard/documents/DocumentDetail'

// Content Hub
import {
  ContentHubAnalytics,
  ContentHubBrowse,
  ContentHubItemDetail,
  ContentHubItemEdit,
  ContentHubItemNew,
  ContentHubSearch,
  ContentHubSettings,
} from './pages/dashboard/content-hub'
import AIStudioPage from './pages/dashboard/AIStudio'
import AIStudioIndex from './pages/dashboard/ai-studio'
import AgentsPage from './pages/dashboard/ai-studio/AgentsPage'
import AIPresetsPage from './pages/dashboard/ai-studio/AIPresetsPage'
import AgentBuilder from './pages/dashboard/ai-studio/AgentBuilder'
import AIChatPage from './pages/dashboard/ai/Chat'
import WorkflowsPage from './pages/dashboard/workflows/Workflows'
import WorkflowBuilderPage from './pages/dashboard/workflows/WorkflowBuilder'
import WorkflowHistoryPage from './pages/dashboard/workflows/WorkflowHistory'
import AnalyticsPage from './pages/dashboard/Analytics'
import BillingPage from './pages/dashboard/billing/Billing'
import BillingCheckoutPage from './pages/dashboard/billing/BillingCheckout'
import SettingsPage from './pages/dashboard/Settings'
import AdminUsersPage from './pages/dashboard/admin/Users'
import AdminTeamsPage from './pages/dashboard/admin/Teams'
import AdminIntegrationsPage from './pages/dashboard/admin/Integrations'
import AdminRolesPage from './pages/dashboard/admin/Roles'
import AdminDataSourcesPage from './pages/dashboard/admin/DataSources'
import AdminBrandGuidelinesPage from './pages/dashboard/admin/BrandGuidelines'
import AdminCompaniesPage from './pages/dashboard/admin/Companies'
import AdminCompanyDetailPage from './pages/dashboard/admin/CompanyDetail'
import ApprovalWorkflowsPage from './pages/dashboard/approval/Workflows'
import PendingApprovalsPage from './pages/dashboard/approval/PendingApprovals'
import ApprovalDetailPage from './pages/dashboard/approval/ApprovalDetail'
import ProfilePage from './pages/dashboard/Profile'
import FeedbackPage from './pages/dashboard/Feedback'
import HelpPage from './pages/dashboard/help/Help'
import HelpDocumentationPage from './pages/dashboard/help/Documentation'
import HelpContactPage from './pages/dashboard/help/Contact'
import BinPage from './pages/dashboard/Bin'
import PresetsPage from './pages/dashboard/presets/Presets'
import DocumentStudioPage from './pages/dashboard/document-studio/DocumentStudio'
import OnboardingWizardPage from './pages/onboarding/OnboardingWizard'
import NotFoundPage from './pages/NotFound'

// Solution pages
import SalesSolutionPage from './pages/solutions/Sales'
import MarketingSolutionPage from './pages/solutions/Marketing'
import OperationsSolutionPage from './pages/solutions/Operations'
import FinanceSolutionPage from './pages/solutions/Finance'

// Marketing pages
import CustomerStoriesPage from './pages/CustomerStories'
import PricingPage from './pages/Pricing'
import AboutPage from './pages/About'
import ContactPage from './pages/Contact'
import PrivacyPage from './pages/Privacy'
import TermsPage from './pages/Terms'

// Beta and Feature Request pages
import JoinBetaPage from './pages/JoinBeta'
import BetaVerifyPage from './pages/BetaVerify'
import FeatureRequestsPage from './pages/FeatureRequests'
import FeatureRequestSubmitPage from './pages/FeatureRequestSubmit'
import AdminBetaRegistrationsPage from './pages/dashboard/admin/BetaRegistrations'
import AdminFeatureRequestsPage from './pages/dashboard/admin/FeatureRequestsAdmin'

// Platform pages
import AIGenerationPage from './pages/platform/AIGeneration'
import TemplatesPage_Platform from './pages/platform/Templates'
import DAMPage from './pages/platform/DAM'
import ExcelPage from './pages/platform/Excel'
import ProductivityPage from './pages/platform/Productivity'
import SecurityPage from './pages/platform/Security'
import IntegrationsPage from './pages/platform/Integrations'
import PlatformAnalyticsPage from './pages/platform/Analytics'

// Industry pages
import LegalIndustryPage from './pages/industries/Legal'
import FinanceIndustryPage from './pages/industries/Finance'
import HealthcareIndustryPage from './pages/industries/Healthcare'
import InsuranceIndustryPage from './pages/industries/Insurance'
import EngineeringIndustryPage from './pages/industries/Engineering'
import ConsultingIndustryPage from './pages/industries/Consulting'

function ProtectedLayout() {
  return (
    <ProtectedRoute>
      <DashboardLayout>
        <Outlet />
      </DashboardLayout>
    </ProtectedRoute>
  )
}

export function App() {
  return (
    <Routes>
      {/* Auth routes (no page transition: they redirect on auth state) */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />

      {/* Public marketing routes, wrapped in the animated page shell */}
      <Route element={<PublicShell />}>
        <Route path="/" element={<HomePage />} />

        {/* Solution pages */}
        <Route path="/solutions/sales" element={<SalesSolutionPage />} />
        <Route path="/solutions/marketing" element={<MarketingSolutionPage />} />
        <Route path="/solutions/operations" element={<OperationsSolutionPage />} />
        <Route path="/solutions/finance" element={<FinanceSolutionPage />} />

        {/* Marketing pages */}
        <Route path="/customers" element={<CustomerStoriesPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />

        {/* Beta and Feature Request pages (public) */}
        <Route path="/join-beta" element={<JoinBetaPage />} />
        <Route path="/join-beta/verify/:token" element={<BetaVerifyPage />} />
        <Route path="/feature-requests" element={<FeatureRequestsPage />} />
        <Route path="/feature-requests/submit" element={<FeatureRequestSubmitPage />} />

        {/* Platform pages */}
        <Route path="/platform/ai-studio" element={<Navigate to="/platform/ai-generation" replace />} />
        <Route path="/platform/ai-generation" element={<AIGenerationPage />} />
        <Route path="/platform/templates" element={<TemplatesPage_Platform />} />
        <Route path="/platform/dam" element={<DAMPage />} />
        <Route path="/platform/excel" element={<ExcelPage />} />
        <Route path="/platform/productivity" element={<ProductivityPage />} />
        <Route path="/platform/security" element={<SecurityPage />} />
        <Route path="/platform/integrations" element={<IntegrationsPage />} />
        <Route path="/platform/analytics" element={<PlatformAnalyticsPage />} />

        {/* Industry pages */}
        <Route path="/industries/legal" element={<LegalIndustryPage />} />
        <Route path="/industries/finance" element={<FinanceIndustryPage />} />
        <Route path="/industries/healthcare" element={<HealthcareIndustryPage />} />
        <Route path="/industries/insurance" element={<InsuranceIndustryPage />} />
        <Route path="/industries/engineering" element={<EngineeringIndustryPage />} />
        <Route path="/industries/consulting" element={<ConsultingIndustryPage />} />

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>

      {/* Onboarding wizard (protected but without dashboard layout) */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute>
            <OnboardingWizardPage />
          </ProtectedRoute>
        }
      />

      {/* Protected dashboard routes */}
      <Route element={<ProtectedLayout />}>
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/templates" element={<TemplateLibraryPage />} />
        <Route path="/templates/new" element={<TemplateNewPage />} />
        <Route path="/templates/:id" element={<TemplateDetailPage />} />
        <Route path="/templates/:id/edit" element={<TemplateEditPage />} />
        <Route path="/templates/:id/analytics" element={<TemplateAnalyticsPage />} />
        <Route path="/hubs/:id" element={<HubDetailPage />} />
        <Route path="/assets" element={<AssetsPage />} />
        <Route path="/documents" element={<DocumentsPage />} />
        <Route path="/documents/:id" element={<DocumentDetailPage />} />

        {/* Content Hub routes */}
        <Route path="/content-hub" element={<ContentHubBrowse />} />
        <Route path="/content-hub/search" element={<ContentHubSearch />} />
        <Route path="/content-hub/analytics" element={<ContentHubAnalytics />} />
        <Route path="/content-hub/item/:itemId" element={<ContentHubItemDetail />} />
        <Route path="/content-hub/item/:itemId/edit" element={<ContentHubItemEdit />} />
        <Route path="/content-hub/new/:type" element={<ContentHubItemNew />} />

        <Route path="/ai-studio" element={<AIStudioIndex />} />
        <Route path="/ai-studio/agents" element={<AgentsPage />} />
        <Route path="/ai-studio/workflows" element={<WorkflowsPage />} />
        <Route path="/ai-studio/presets" element={<AIPresetsPage />} />
        <Route path="/ai-studio/builder" element={<AgentBuilder />} />
        <Route path="/ai-studio/builder/:id" element={<AgentBuilder />} />
        <Route path="/ai-studio/content" element={<AIStudioPage />} />
        <Route path="/ai-studio/chat/:agentSlug" element={<AIChatPage />} />
        <Route path="/ai/chat" element={<AIChatPage />} />
        <Route path="/workflows" element={<WorkflowsPage />} />
        <Route path="/workflows/builder" element={<WorkflowBuilderPage />} />
        <Route path="/workflows/:id/history" element={<WorkflowHistoryPage />} />
        <Route path="/billing" element={<BillingPage />} />
        <Route path="/billing/checkout" element={<BillingCheckoutPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/admin/analytics" element={<AnalyticsPage />} />
        <Route path="/admin/companies" element={<AdminCompaniesPage />} />
        <Route path="/admin/companies/:id" element={<AdminCompanyDetailPage />} />
        <Route path="/admin/users" element={<AdminUsersPage />} />
        <Route path="/admin/teams" element={<AdminTeamsPage />} />
        <Route path="/admin/integrations" element={<AdminIntegrationsPage />} />
        <Route path="/admin/roles" element={<AdminRolesPage />} />
        <Route path="/admin/data-sources" element={<AdminDataSourcesPage />} />
        <Route path="/admin/brand-guidelines" element={<AdminBrandGuidelinesPage />} />
        <Route path="/admin/beta-registrations" element={<AdminBetaRegistrationsPage />} />
        <Route path="/admin/feature-requests" element={<AdminFeatureRequestsPage />} />
        <Route path="/admin/content-hub-settings" element={<ContentHubSettings />} />
        <Route path="/approval/workflows" element={<ApprovalWorkflowsPage />} />
        <Route path="/approval/pending" element={<PendingApprovalsPage />} />
        <Route path="/approval/:id" element={<ApprovalDetailPage />} />
        <Route path="/profile" element={<ProfilePage />} />
        <Route path="/feedback" element={<FeedbackPage />} />
        <Route path="/help" element={<HelpPage />} />
        <Route path="/help/documentation" element={<HelpDocumentationPage />} />
        <Route path="/help/contact" element={<HelpContactPage />} />
        <Route path="/bin" element={<BinPage />} />
        <Route path="/presets" element={<PresetsPage />} />
        <Route path="/document-studio" element={<DocumentStudioPage />} />
      </Route>
    </Routes>
  )
}
